import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";

import { Pagination, SortChangedEvent, Table } from "@performyard/py-components";

// components
import DownloadButton from "../../../../../components.smart/DownloadButton/DownloadButton";
import ThumbSwitch from "../../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageHeading from "../../../../../components/PageHeading";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Typography from "../../../../../components/Text";
import getAssignmentsCols from "./components/AssignmentsCols";

import { SearchServiceSort } from "../../../../../api/hooks";
import { useIsNewNavigation } from "../../../../../api/hooks/defs/navigation";
import { useReviewAssignments } from "../../../../../api/hooks/defs/review-assignments";

// template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import { CompanyReviewsNavigationLinks, REVIEWS_TABLE_LIMIT } from "../../constants";
// css
import css from "./Assignments.module.scss";

export type Assignment = {
	current_author_assigned: number;
	current_author_overdue_assigned: number;
	current_signer_assigned: number;
	current_signer_overdue_assigned: number;
	lower_name_searchable: string;
	lower_name_sortable: string;
	name: string;
	reports_counts: number;
	waiting_author_assigned: number;
	waiting_author_overdue: number;
	waiting_signer_assigned: number;
	waiting_signer_overdue: number;
	_id: string;
};

type AssignmentsProps = {
	navLinks: SideNavigationLink[];
};

const PAGE_TITLE = "Review Assignments";

const Assignments = ({ navLinks }: AssignmentsProps) => {
	const { page } = useParams<{ page: string }>();
	const isNewNav = useIsNewNavigation();

	const [showOnlyOverdue, setShowOnlyOverdue] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [currentSort, setCurrentSort] = useState<SearchServiceSort>({
		name: "lower_name_sortable",
		direction: "asc",
	});
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [searchValue] = useDebounceValue(searchTerm, 500);

	const [downloadingCSV, setDownloadingCSV] = useState(false);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchValue]);

	const { data: assignmentsData, isLoading } = useReviewAssignments(
		showOnlyOverdue,
		searchValue,
		currentSort,
		(currentPage - 1) * REVIEWS_TABLE_LIMIT,
		REVIEWS_TABLE_LIMIT
	);

	const assignments = (assignmentsData?.assignments as Assignment[]) || [];
	const assignmentsCount = assignmentsData?.count[0]?.count || 0;

	const columnDefs = useMemo(() => getAssignmentsCols(), []);

	const onSortChanged = (event: SortChangedEvent<Assignment>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setCurrentSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchTerm}
			onSearchInputChange={(value) => setSearchTerm(value)}
			activeFiltersCount={0}
			searchPlaceholder="Search employees..."
		/>
	);

	const csvFilters = `&employee_name_search=${searchTerm}${
		currentSort.name ? `&sortName=${currentSort.name}&sortDirection=${currentSort.direction}` : ""
	}`;

	const actionButtons = [
		<div className={css.onlyOverdueSwitch} key="overdue-switch">
			<Typography weight="semi-bold" size="paragraph-3">
				Show Only Overdue
			</Typography>
			<ThumbSwitch value={showOnlyOverdue} onChange={() => setShowOnlyOverdue(!showOnlyOverdue)} />
		</div>,
		<div className={css.downloadButton} key="downloadButton">
			<a href={`/company/reviews/overview.csv/?${csvFilters}`}>
				<DownloadButton
					onClick={() => setDownloadingCSV(true)}
					style={{ height: "32px" }}
					isJobRunning={downloadingCSV}
					testid="download-csv"
				>
					Download CSV
				</DownloadButton>
			</a>
		</div>,
	];

	const mainContent = (
		<div>
			<Table<Assignment>
				autoHeight
				columnSizing="fit-grid"
				rowData={assignments}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Assignments Found"
				initialState={{
					sort: {
						sortModel: currentSort.name
							? [{ colId: currentSort.name, sort: currentSort.direction as "asc" | "desc" }]
							: [],
					},
				}}
				animateRows={false}
			/>
			{assignmentsCount > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						pageCount={Math.ceil(assignmentsCount / REVIEWS_TABLE_LIMIT)}
						currentPage={currentPage}
						onPageChange={(page: number) => setCurrentPage(page)}
					/>
				</div>
			)}
		</div>
	);

	return (
		<div>
			<InfoPageTemplate
				pageTitle={PAGE_TITLE}
				pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
				toolbarActionRow={toolbarActionRow}
				actionButtons={actionButtons}
				mainContent={mainContent}
				navigationLinks={navLinks || CompanyReviewsNavigationLinks}
				activeNavigationLinkKey={page}
			/>
		</div>
	);
};

export default Assignments;
